import { useCallback, useMemo } from 'react';
import { useQueryUsageInformation } from 'src/services/queries/useQueryPayment';
import { FeatureAction } from 'src/utils/constants/featuresPoint';
import { ERROR_CODE } from 'src/utils/constants/statusCode';

const normalizeUrl = (url: string) => {
  return url.replace(/\/+$/, '').replace(/^\/+/, '').toLowerCase();
};

export const useFeatureAccess = () => {
  const { data: usageInformation } = useQueryUsageInformation();
  const usageFeatures = usageInformation?.data?.features || [];

  const grantedEndpoints = useMemo(() => {
    if (usageInformation?.data?.id === 0) return null; // Full Access
    return new Set(usageFeatures.flatMap((f) => (f.endpoints || []).map(normalizeUrl)));
  }, [usageFeatures, usageInformation?.data?.id]);

  const hasAccess = useCallback(
    (endpoints?: string[]) => {
      if (!endpoints || endpoints.length === 0 || grantedEndpoints === null) return true;
      return endpoints.some((endpoint) => grantedEndpoints.has(normalizeUrl(endpoint)));
    },
    [grantedEndpoints],
  );

  const getFeaturePoints = useCallback(
    (endpoint: string, action: FeatureAction = FeatureAction.VIEW) => {
      const normalizedEndpoint = normalizeUrl(endpoint);

      const feature = usageFeatures.find((f) =>
        (f.endpoints || []).some((e) => normalizeUrl(e) === normalizedEndpoint),
      );

      const points = (() => {
        if (!feature) return 0;
        switch (action) {
          case FeatureAction.VIEW:
            return feature.points_view || 0;
          case FeatureAction.DOWNLOAD:
            return feature.points_download || 0;
          default:
            return 0;
        }
      })();

      return {
        planName:
          usageInformation?.status_code === ERROR_CODE.NOT_FOUND
            ? undefined
            : usageInformation?.data?.current_product,
        creditCosts: points,
      };
    },
    [usageFeatures, usageInformation],
  );

  return {
    hasAccess,
    getFeaturePoints,
    isFullAccess: grantedEndpoints === null,
    usageInformation,
  };
};
